import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../config/supabase';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // Cache และ debounce สำหรับป้องกันการ fetch ซ้ำ
    const lastFetchTimeRef = React.useRef(0);
    const fetchingRef = React.useRef(false);
    const userIdRef = React.useRef(null); // Track current user ID to prevent unnecessary re-renders

    useEffect(() => {
        // If Supabase is not configured, stop loading immediately
        if (!isSupabaseConfigured()) {
            setLoading(false);
            return;
        }

        const getInitialSession = async () => {
            try {
                setLoading(true);
                const { data: { session }, error } = await supabase.auth.getSession();

                if (error) {
                    console.error('❌ [AuthContext] Session error:', error.message);
                }

                setUser(session?.user || null);

                if (session?.user) {
                    await fetchUserProfile(session.user.id);
                } else {
                    setUserProfile(null);
                }
            } catch (error) {
                console.error('❌ [AuthContext] Unexpected error:', error);
            } finally {
                setLoading(false);
            }
        };

        getInitialSession();

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, session) => {
                console.log(`🔔 [AuthContext] Auth event: ${event}`);

                if (event === 'SIGNED_OUT') {
                    setUser(null);
                    setUserProfile(null);
                    setLoading(false);
                    return;
                }

                // Skip profile refetch for token refresh if we already have profile data
                // This prevents timeout errors when switching tabs
                if (event === 'TOKEN_REFRESHED') {
                    console.log(`✅ [AuthContext] Token refreshed, keeping existing profile`);
                    setUser(session?.user || null);
                    // Don't fetch profile again, just update user
                    return;
                }

                // Only show loading for sign-in events if it's actually a new sign-in
                if (event === 'SIGNED_IN') {
                    // Check if we already have this user loaded
                    if (session?.user?.id === userIdRef.current) {
                        console.log('✅ [AuthContext] Same user session restored, skipping loading state');
                        // Just ensure user state is up to date, but don't trigger loading
                        setUser(session.user);
                        return;
                    }
                    setLoading(true);
                }

                const currentUser = session?.user || null;
                setUser(currentUser);
                userIdRef.current = currentUser?.id || null; // Update ref

                if (currentUser) {
                    await fetchUserProfile(currentUser.id);
                } else {
                    setUserProfile(null);
                }

                setLoading(false);
            }
        );

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const fetchUserProfile = async (userId) => {
        // ป้องกันการ fetch ซ้ำภายใน 2 วินาที
        const now = Date.now();
        if (now - lastFetchTimeRef.current < 2000) {
            console.log('⏭️ [AuthContext] Skipping fetch - too soon after last fetch');
            return;
        }

        // ป้องกันการ fetch พร้อมกัน
        if (fetchingRef.current) {
            console.log('⏭️ [AuthContext] Already fetching profile, skipping...');
            return;
        }

        fetchingRef.current = true;
        lastFetchTimeRef.current = now;

        // REDUCED TIMEOUT TO 5 SECONDS for faster feedback
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('DB_TIMEOUT')), 5000);
        });

        try {
            console.log('🔵 [AuthContext] Fetching profile...');

            const queryPromise = supabase
                .from('users')
                .select('id, full_name, email, role, team_id') // Select specific fields to be safe
                .eq('id', userId)
                .single();

            const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

            if (error) {
                // Handle "Row not found" specifically - this is normal for new users
                if (error.code === 'PGRST116') {
                    console.warn('⚠️ [AuthContext] Profile not found. Using fallback.');
                    setUserProfile({
                        id: userId,
                        full_name: 'New User',
                        email: user?.email || '',
                        role: 'Sales'
                    });
                    fetchingRef.current = false;
                    return;
                }
                throw error;
            }

            console.log('✅ [AuthContext] Success');
            setUserProfile(data);

        } catch (error) {
            console.error('❌ [AuthContext] Error:', error.message || error);

            let fallbackName = 'User (Offline)';

            // Handle specific known errors
            if (error.code === '42P17') {
                fallbackName = 'System Error: RLS Recursion';
            } else if (error.code === '42703') {
                fallbackName = 'System Error: Migrations Missing';
            } else if (error.message === 'DB_TIMEOUT') {
                fallbackName = 'System Error: Timeout';
                console.error('🚨 DATABASE LOCK DETECTED! Run the emergency-rls-fix.sql migration!');
            }

            // FALLBACK PROFILE so the app doesn't crash/hang
            setUserProfile({
                id: userId,
                full_name: fallbackName,
                email: '',
                role: 'Sales'
            });
        } finally {
            fetchingRef.current = false;
        }
    };

    const signIn = async (email, password) => {
        if (!isSupabaseConfigured()) return { error: { message: 'No Supabase config' } };
        return supabase.auth.signInWithPassword({ email, password });
    };

    const signUp = async (email, password, userData) => {
        if (!isSupabaseConfigured()) return { error: { message: 'No Supabase config' } };
        const { data, error } = await supabase.auth.signUp({ email, password });

        if (data.user && !error) {
            // Create profile immediately
            await supabase.from('users').insert({
                id: data.user.id,
                email,
                ...userData
            });
        }
        return { data, error };
    };

    const signOut = async () => {
        if (!isSupabaseConfigured()) return { error: { message: 'No Supabase config' } };
        setUser(null);
        setUserProfile(null);
        return supabase.auth.signOut();
    };

    return (
        <AuthContext.Provider value={{ user, userProfile, loading, signIn, signUp, signOut }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
